import { Button, Form, Table } from "antd";
import { useEffect, useState } from "react";
import { SaveOutlined } from '@ant-design/icons';
import { IEquipmentSendingItem, IEquipmentSendingItemProps, IEquipmentSendingItemResult, IEquipmentSendingItemStates } from "./interfaces";
import { Lookup } from "../../components/lookup";
import { enmControlType, enmPropertyType } from "../../components/entityManager/interfaces";
import { DamageType } from "../damageType";
import { Core } from "../../components/infrastructure/core";
import { useContext } from "react";
import AppContext from "../../components/infrastructure/appContext";
import { enmEntityState, enmValidationLevel, ICallOptions, IResult, IResultSet } from "../../components/infrastructure/models";
import { Redirect } from "react-router-dom";
import { IDamageType } from "../damageType/interfaces";
import { ISendingItem } from "../sendingItem/interfaces";
import './index.css';

type LayoutType = Parameters<typeof Form>[0]['layout'];

const EquipmentSendingItem = (props: IEquipmentSendingItemProps) => {
    const context = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [state, setState] = useState<IEquipmentSendingItemStates>({
        loadData: true,
        isAuthorized: true,
        selectedItems: [],
        sendingItems: [],
        equipmentSendingItems: [],
        saveAccess: false,
        currentEquipmentId: props.params().equipment?.Id
    });
    const { loadData } = state;
    const [form] = Form.useForm();
    const [formLayout] = useState<LayoutType>('horizontal');
    const formItemLayout = {
        labelCol: { span: 4 },
        wrapperCol: { span: 18 },
    };

    useEffect(() => {
        if (props.params().equipment.Id !== state.currentEquipmentId) {
            setState({
                loadData: true,
                isAuthorized: true,
                selectedItems: [],
                sendingItems: [],
                equipmentSendingItems: [],
                saveAccess: false,
                currentDamageTypeId: state.currentDamageTypeId,
                currentEquipmentId: props.params().equipment.Id
            });
            setLoading(true);
        }
    }, [props]);

    if (!state.isAuthorized) {
        let loginUrl = '/login';
        if (context?.path)
            loginUrl += '?' + encodeURIComponent(context.path);
        return (<Redirect to={loginUrl} />);
    }

    if (loadData) {
        let res: IResultSet<IEquipmentSendingItemResult> | null = null;

        let opt: ICallOptions = {
            url: "equipmentsendingitem/getequipments",
            data: { equipmentId: props.params().equipment.Id, damageTypeId: state.currentDamageTypeId },
            onSuccess: (result) => {
                res = result as any;
            },
            onComplete: () => {
                if (res && res.Data) {
                    if (!res.ValidationState.Successful) {
                        let isAuthorized = true;
                        if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.AccessDenied || v.Level === enmValidationLevel.Unauthorized)) {
                            Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                            isAuthorized = false;
                        }
                        setState({
                            loadData: false,
                            isAuthorized: isAuthorized,
                            selectedItems: [],
                            sendingItems: [],
                            equipmentSendingItems: [],
                            saveAccess: false
                        });
                        setLoading(false);
                        return;
                    }

                    let selectedItems = res.Data.EquipmentSendingItems.map(e => {
                        return res!.Data!.SendingItems.find(d => d.Id === e.SendingItemId)?.RowGuid!;
                    });

                    setState({
                        loadData: false,
                        isAuthorized: true,
                        sendingItems: res.Data.SendingItems,
                        selectedItems: selectedItems,
                        equipmentSendingItems: res.Data.EquipmentSendingItems,
                        saveAccess: res.Data.SaveAccess,
                        currentDamageTypeId: (form.getFieldValue("DamageType") as IDamageType)?.Id,
                        currentEquipmentId: props.params().equipment?.Id
                    });
                    setLoading(false);
                    return;
                }
                setState({
                    loadData: false,
                    isAuthorized: true,
                    sendingItems: [],
                    selectedItems: [],
                    equipmentSendingItems: [],
                    saveAccess: false,
                    currentEquipmentId: props.params().equipment?.Id
                });
                setLoading(false);
            }
        };
        Core.callApi(opt);
    }

    const save = (changes: IEquipmentSendingItem[]) => {
        if (!changes.length) {
            Core.warningNotification("لطفا ابتدا اطلاعات را تغییر دهید");
            return;
        }
        setLoading(true);
        let res: IResult | null = null;

        let opt: ICallOptions = {
            url: "equipmentsendingitem/save",
            data: { entities: changes },
            onSuccess: (result) => {
                res = result;
            },
            onComplete: () => {
                if (res) {
                    if (!res.ValidationState.Successful) {
                        let isAuthorized = true;
                        if (res.ValidationState.ValidationResults.find(v => v.Level === enmValidationLevel.AccessDenied || v.Level === enmValidationLevel.Unauthorized)) {
                            Core.errorNotification(res.ValidationState.ValidationResults[0].Message!);
                            isAuthorized = false;
                        }
                        setState({
                            loadData: false,
                            isAuthorized: isAuthorized,
                            selectedItems: [],
                            sendingItems: [],
                            equipmentSendingItems: [],
                            saveAccess: false
                        });
                        setLoading(false);
                        return;
                    }
                    Core.successNotification("اطلاعات با موفقیت ذخیره شد");
                    setState({
                        loadData: true,
                        isAuthorized: true,
                        sendingItems: [],
                        selectedItems: [],
                        equipmentSendingItems: [],
                        saveAccess: false,
                        currentDamageTypeId: state.currentDamageTypeId,
                        currentEquipmentId: state.currentEquipmentId
                    });
                    setLoading(true);
                    return;
                }
                setState({
                    loadData: false,
                    isAuthorized: true,
                    sendingItems: [],
                    selectedItems: [],
                    equipmentSendingItems: [],
                    saveAccess: false,
                    currentEquipmentId: state.currentEquipmentId
                });
                setLoading(false);
            }
        };
        Core.callApi(opt);
    };

    return (
        <Form
            {...formItemLayout}
            layout={formLayout}
            form={form}
            initialValues={{ layout: formLayout }}
            onFinish={() => {
                let changes: IEquipmentSendingItem[] = [];
                state.selectedItems.forEach(item => {
                    let sendingItem = state.sendingItems.find(d => d.RowGuid === item);
                    if (sendingItem && !state.equipmentSendingItems.find(r => r.SendingItemId === sendingItem?.Id)) {
                        changes.push({ RowGuid: Core.newId(), DamageTypeId: state.currentDamageTypeId!, SendingItemId: sendingItem.Id, EquipmentId: state.currentEquipmentId!, State: enmEntityState.Added } as any);
                    }
                });
                state.equipmentSendingItems.forEach(item => {
                    let sendingItem = state.sendingItems.find(d => d.Id === item.SendingItemId);
                    if (sendingItem && !state.selectedItems.find(r => r === sendingItem?.RowGuid)) {
                        changes.push({ Id: item.Id, RowGuid: item.RowGuid, RowVrsn: item.RowVrsn, State: enmEntityState.Deleted } as any);
                    }
                });

                save(changes);
            }}
            style={{ margin: 10 }}
        >
            <Form.Item
                label={"نوع خسارت"}
                name={"DamageType"}
                rules={[{ required: true, message: "لطفا نوع خسارت را انتخاب نمایید" }]}
                key={"DamageType"}
                style={{ margin: 10 }}
            >
                <Lookup
                    onChange={(value?: IDamageType) => {
                        setState({
                            loadData: true,
                            isAuthorized: true,
                            sendingItems: [],
                            selectedItems: [],
                            equipmentSendingItems: [],
                            saveAccess: false,
                            currentDamageTypeId: value?.Id,
                            currentEquipmentId: props.params().equipment?.Id
                        });
                        setLoading(true);
                    }}
                    isLookupMode={true}
                    property={{
                        controlType: enmControlType.lookup,
                        propertyType: enmPropertyType.number,
                        dataIndex: "DamageType",
                        title: "نوع خسارت",
                        lookup: {
                            lookupComponent: DamageType,
                            lookupProperty: "DamageType",
                            lookupDisplayFields: ["Name"]
                        }
                    }}
                    parent={props.parent}
                    key={"DamageType"}
                />
            </Form.Item>

            <Form.Item
                label={"موارد ارسالی"}
                name={"SendingItem"}
                key={"SendingItem"}
                style={{ margin: 10 }}
            >
                <Table
                    rowKey="RowGuid"
                    onHeaderRow={() => {
                        return {
                            className: 'ant-table-thead-checkbox',
                        };
                    }}
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: state.selectedItems,
                        onChange: (selectedRowKeys: React.Key[], _: ISendingItem[]) => {
                            setState({
                                loadData: false,
                                isAuthorized: true,
                                sendingItems: state.sendingItems,
                                selectedItems: selectedRowKeys,
                                equipmentSendingItems: state.equipmentSendingItems,
                                saveAccess: state.saveAccess,
                                currentDamageTypeId: state.currentDamageTypeId,
                                currentEquipmentId: state.currentEquipmentId
                            });
                            setLoading(false);
                        },
                    }}
                    pagination={{
                        style: { marginLeft: 10 }
                    }}
                    dataSource={state.sendingItems}
                    className="box"
                    columns={[
                        {
                            dataIndex: "Name",
                            title: "موارد ارسالی"
                        }
                    ]}
                    loading={loading}
                />
            </Form.Item>

            <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ borderTop: '1px solid', borderColor: '#e5e5e5', marginLeft: 20 }}>
                <Button disabled={!state.saveAccess} type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} style={{ lineHeight: '0.5em', verticalAlign: 'middle', width: "100px", marginTop: 10 }}>
                    ذخیره
                </Button>
            </Form.Item>
        </Form>
    );
}

export default EquipmentSendingItem;